import { create } from "zustand";

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_LAYOUT,
  LAYOUTS,
} from "../features/studio/layouts";
import {
  activeScene,
  layersFromPreset,
  newId,
  readSceneBook,
  sceneFromPreset,
  type Layer,
  type SceneBook,
  type StudioScene,
} from "../features/studio/scene";
import { readStored, writeStored } from "../lib/persisted";
import type { BannerPreset } from "../features/profile/banner";

/**
 * Whether the studio is open, and how it is set up.
 *
 * ## Why this is not component state
 *
 * It was, and that made a broadcast a property of the console page: navigating
 * anywhere - a profile, the inbox, a track - unmounted the studio, which
 * stopped the camera, tore down the peer connection and ended the show. A host
 * who clicked a notification lost their set.
 *
 * The engine now lives in the app shell, above the router, the way the audio
 * player already does. That means the two ends of it - the shell that runs it
 * and the console that draws its controls - need somewhere to agree, and this
 * is that place.
 *
 * ## Settings live here too
 *
 * Layout, background and levels are read by the compositor in the shell and
 * written by the panel in the console. Keeping them next to `open` means the
 * studio survives navigation *as it was configured*, rather than resetting to
 * the default layout every time the host looks at something.
 *
 * Devices and the connection are deliberately *not* here: they are objects with
 * lifetimes rather than values, and they belong to the engine.
 */
interface StudioState {
  /** Whether the host has opened the studio. Not whether it is broadcasting. */
  open: boolean;

  /**
   * What is on screen, and where.
   *
   * A book rather than a single scene, because hosts will want the
   * intermission/be-right-back switching every other broadcast tool has, and
   * the container is far cheaper to build now than to migrate onto saved
   * layouts later. The console shows one scene today.
   */
  book: SceneBook;

  deckLevel: number;
  micLevel: number;
  /** Empty means the system default. */
  camera: string;
  microphone: string;

  setOpen: (open: boolean) => void;
  set: <K extends keyof StudioSettings>(
    key: K,
    value: StudioSettings[K],
  ) => void;

  /** Replace the active scene's layers with one of the shipped presets. */
  applyPreset: (presetId: string) => void;
  /**
   * Change one layer of the active scene.
   *
   * Anything positional marks the scene as no longer being its preset -
   * see `presetId` - while a visibility toggle does not, because hiding the
   * name badge on "Split" leaves it recognisably Split.
   */
  updateLayer: (id: string, patch: Partial<Layer>) => void;
  /** Put an uploaded image on the frame, and return the layer it became. */
  addImage: (image: { url: string; width: number; height: number }) => void;
  removeLayer: (id: string) => void;
  /** Move a layer one step through the z-order. Negative is backwards. */
  moveLayer: (id: string, delta: number) => void;
  setBackground: (background: BannerPreset | null) => void;
}

/** The scalar settings, which are the ones `set` handles. */
type StudioSettings = Pick<
  StudioState,
  "deckLevel" | "micLevel" | "camera" | "microphone"
>;

const STORAGE_KEY = "gv.studio";

const DEFAULTS: StudioSettings = {
  deckLevel: 0.8,
  micLevel: 1,
  camera: "",
  microphone: "",
};

/**
 * The setup a host chose last time, checked before it is trusted.
 *
 * ## Why `open` is not among them
 *
 * Not because reopening would be wrong - it is restored, from the console's
 * remembered broadcast mode, and the first version of this comment gave the
 * wrong reason for not doing so. Opening the studio does *not* ask for the
 * camera or the microphone: those start from their own buttons and from
 * nothing else, so no webcam light comes on because somebody refreshed.
 *
 * It is not stored here because there would then be two records of the same
 * thing. `broadcastMode` in the console store already says whether this host
 * broadcasts from the studio, and a second copy in a second key is a copy that
 * can disagree - which it did, visibly: the Studio pill came back selected
 * beside a studio column that was not there.
 *
 * ## Why every field is checked
 *
 * What comes back was written by whatever build the host last ran, and a value
 * that has since changed shape reaches the compositor, which draws on a canvas
 * thirty times a second and is a bad place to discover `undefined`. The scene
 * book gets the same treatment in `readSceneBook`, layer by layer.
 */
function restore(): StudioSettings & { book: SceneBook } {
  const saved = readStored<Record<string, unknown>>(STORAGE_KEY);

  const str = (v: unknown, fallback: string) =>
    typeof v === "string" ? v : fallback;
  // Levels are gains, and a stored NaN or a negative would be silence or worse.
  const level = (v: unknown, fallback: number) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 2
      ? v
      : fallback;

  return {
    book: readSceneBook(saved.book) ?? migrate(saved),
    deckLevel: level(saved.deckLevel, DEFAULTS.deckLevel),
    micLevel: level(saved.micLevel, DEFAULTS.micLevel),
    /*
     * Device ids are stored and restored, but they are the least reliable thing
     * here: a browser reissues them when site permissions are reset, and the
     * webcam that produced this one may simply not be plugged in today. The
     * engine falls back to the default device and clears the stale value rather
     * than failing to start - see `startCamera` in `useStudio`.
     */
    camera: str(saved.camera, DEFAULTS.camera),
    microphone: str(saved.microphone, DEFAULTS.microphone),
  };
}

/**
 * Carry a pre-layers setup forward.
 *
 * The studio used to store `layoutId`, `showCard` and `showBrand` as three flat
 * values. Anyone who had configured it before layers existed has those in their
 * browser right now, and dropping them would silently reset the frame of every
 * host who had set one up - the exact thing persistence was added to stop.
 *
 * So the old three become what they always meant: a preset, with two of its
 * layers possibly hidden.
 */
function migrate(saved: Record<string, unknown>): SceneBook {
  const preset =
    LAYOUTS.find((l) => l.id === saved.layoutId) ?? DEFAULT_LAYOUT;

  const scene = sceneFromPreset(preset, banner(saved.background));

  for (const layer of scene.layers) {
    if (layer.kind === "card" && saved.showCard === false) layer.visible = false;
    if (layer.kind === "brand" && saved.showBrand === false) {
      layer.visible = false;
    }
  }

  return { version: 1, scenes: [scene], activeId: scene.id };
}

/** A stored background, or null unless it is genuinely one. */
function banner(value: unknown): BannerPreset | null {
  if (typeof value !== "object" || value === null) return null;

  const preset = value as Partial<BannerPreset>;

  const usable =
    typeof preset.preset === "string" &&
    typeof preset.angle === "number" &&
    Number.isFinite(preset.angle) &&
    Array.isArray(preset.stops) &&
    preset.stops.length > 0 &&
    preset.stops.every((stop) => typeof stop === "string");

  return usable ? (preset as BannerPreset) : null;
}

/**
 * The layers a preset put there, as opposed to the ones the host added.
 *
 * ## Why the distinction earns its keep
 *
 * "Split" is a statement about where the camera and the now-playing card sit.
 * A logo the host dropped on top is not part of that statement, so adding,
 * moving or removing one does not stop the scene being Split - and picking
 * Split again should not throw the logo away.
 *
 * Both of those were wrong in the first cut, and they compounded: adding an
 * image cleared the preset highlight, so the host clicked their layout again to
 * put it back, and that wiped the image they had just added.
 */
const PRESET_KINDS = ["camera", "card", "brand"] as const;

function fromPreset(layer: Layer): boolean {
  return (PRESET_KINDS as readonly string[]).includes(layer.kind);
}

/**
 * Apply a change to the active scene, leaving the rest of the book alone.
 *
 * Every scene mutation goes through here so that "which scene is active" is
 * resolved in exactly one place. Written as a copy rather than a mutation
 * because the compositor memoises on the scene it was handed, and editing a
 * layer in place would produce a frame that never repaints.
 */
function editScene(
  book: SceneBook,
  edit: (scene: StudioScene) => StudioScene,
): SceneBook {
  const current = activeScene(book);

  return {
    ...book,
    scenes: book.scenes.map((scene) =>
      scene.id === current.id ? edit(scene) : scene,
    ),
  };
}

/**
 * Where a newly added image starts.
 *
 * ## Its own shape, centred, at half size
 *
 * The aspect ratio comes from the file, so nothing arrives stretched - a logo
 * added and immediately squashed into a rectangle the code guessed is one the
 * host has to repair before they can even judge it.
 *
 * Half the frame is a deliberate compromise between the two things people add.
 * A corner badge wants to be smaller and a background wants to fill the frame,
 * and there is no way to tell which this is from its pixels - a 1920x1080 file
 * is equally likely to be either. Half is visibly *placed* rather than
 * accidentally full-bleed, which makes the next action obvious: drag it.
 */
function imageLayer(image: { url: string; width: number; height: number }): Layer {
  const aspect = image.width > 0 && image.height > 0
    ? image.width / image.height
    : 16 / 9;

  // Fitted inside half the frame rather than covering it, so the whole picture
  // is visible at the moment it appears.
  const frameAspect = CANVAS_WIDTH / CANVAS_HEIGHT;
  const w = aspect >= frameAspect ? 0.5 : (0.5 * aspect) / frameAspect;
  const h = aspect >= frameAspect ? (0.5 * frameAspect) / aspect : 0.5;

  return {
    id: newId(),
    kind: "image",
    box: { x: (1 - w) / 2, y: (1 - h) / 2, w, h },
    visible: true,
    src: image.url,
    // `contain` is redundant at this exact box - it already matches the
    // picture's aspect - and stops becoming redundant the moment the host
    // resizes, which is when cropping their logo would surprise them.
    fit: "contain",
    opacity: 1,
  };
}

export const useStudioSettings = create<StudioState>()((set) => ({
  open: false,

  ...restore(),

  setOpen: (open) => set({ open }),
  set: (key, value) => set({ [key]: value } as Partial<StudioState>),

  applyPreset: (presetId) =>
    set((state) => {
      const preset = LAYOUTS.find((l) => l.id === presetId);

      if (!preset) return state;

      return {
        book: editScene(state.book, (scene) => ({
          ...scene,
          presetId: preset.id,
          name: preset.label,
          /*
           * The preset's own layers are replaced; anything the host added is
           * kept, and kept on top where they put it. Choosing a different
           * arrangement for the camera and the card is not a request to delete
           * their logo.
           */
          layers: [
            ...layersFromPreset(preset),
            ...scene.layers.filter((layer) => !fromPreset(layer)),
          ],
        })),
      };
    }),

  updateLayer: (id, patch) =>
    set((state) => ({
      book: editScene(state.book, (scene) => {
        /*
         * Moving or resizing one of the preset's *own* layers means this is no
         * longer that preset, and the picker should stop claiming it is.
         *
         * Two things deliberately do not clear it. Hiding a layer: "Split" with
         * the name badge switched off is still recognisably Split. And dragging
         * an image the host added: where their logo sits says nothing about
         * where the camera and card are, which is all a preset describes.
         */
        const moved = scene.layers.find((layer) => layer.id === id);
        const positional =
          ("box" in patch || "shape" in patch || "style" in patch) &&
          moved !== undefined &&
          fromPreset(moved);

        return {
          ...scene,
          presetId: positional ? null : scene.presetId,
          layers: scene.layers.map((layer) =>
            layer.id === id ? ({ ...layer, ...patch } as Layer) : layer,
          ),
        };
      }),
    })),

  addImage: (image) =>
    set((state) => ({
      book: editScene(state.book, (scene) => ({
        ...scene,
        // On top, because that is what "add" means. Anything else and the
        // picture a host just chose is behind what was already there.
        layers: [...scene.layers, imageLayer(image)],
      })),
    })),

  removeLayer: (id) =>
    set((state) => ({
      book: editScene(state.book, (scene) => ({
        ...scene,
        // Only clears the preset if the layer removed was one of *its* layers.
        presetId: scene.layers.some(
          (layer) => layer.id === id && fromPreset(layer),
        )
          ? null
          : scene.presetId,
        layers: scene.layers.filter((layer) => layer.id !== id),
      })),
    })),

  moveLayer: (id, delta) =>
    set((state) => ({
      book: editScene(state.book, (scene) => {
        const from = scene.layers.findIndex((layer) => layer.id === id);
        const to = from + delta;

        // Clamped rather than wrapped: a layer at the front that jumps to the
        // back because somebody pressed forward once more is a surprise, and an
        // out-of-range splice would silently drop it.
        if (from < 0 || to < 0 || to >= scene.layers.length) return scene;

        const layers = [...scene.layers];
        const [moved] = layers.splice(from, 1);

        layers.splice(to, 0, moved!);

        // Restacking says nothing about where the camera and card sit, which is
        // all a preset claims.
        return { ...scene, layers };
      }),
    })),

  setBackground: (background) =>
    set((state) => ({
      book: editScene(state.book, (scene) => ({ ...scene, background })),
    })),
}));

/**
 * Keep what the host chose.
 *
 * Subscribed at module load rather than from an effect, because these settings
 * outlive every component that edits them - the panel unmounts on navigation
 * while the broadcast carries on, and a subscription tied to it would stop
 * saving at exactly that point.
 */
useStudioSettings.subscribe((state) => {
  writeStored(STORAGE_KEY, {
    book: state.book,
    deckLevel: state.deckLevel,
    micLevel: state.micLevel,
    camera: state.camera,
    microphone: state.microphone,
  });
});
